'use client';

import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useDropzone } from 'react-dropzone';

import { useRouteContext } from '../../hooks/useRouteContext';

// Actions et services
import fileService from '../../services/fileService';
import submissionService from '../../services/submissionService';
import { createSubmission, updateSubmission, getSubmissionById, clearSubmission } from '../../redux/actions/submissionActions';

const SubmissionSchema = Yup.object().shape({
  name: Yup.string()
    .required('Le nom est requis')
    .max(100, 'Le nom ne peut pas dépasser 100 caractères'),
  description: Yup.string()
    .max(1000, 'La description ne peut pas dépasser 1000 caractères'),
});

const SubmissionForm = () => {

  const { getContextualPath } = useRouteContext();
  const [filesMarkedForRemoval, setFilesMarkedForRemoval] = useState([]);

  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { submission, loading } = useSelector(state => state.submissions);

  // États pour la gestion des fichiers multiples
  const [files, setFiles] = useState([]);
  const [fileIds, setFileIds] = useState([]);
  const [fileUploading, setFileUploading] = useState(false);
  const [fileError, setFileError] = useState(null);
  const [stlValidationError, setStlValidationError] = useState(false); // Nouvel état pour l'erreur STL
  const [dimensions, setDimensions] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [removingFiles] = useState([]);

  const maxFiles = 10;
  const maxSize = parseInt(process.env.REACT_APP_MAX_FILE_SIZE) || 50 * 1024 * 1024;

  // Validation pour vérifier qu'au moins un fichier 3D est présent (STL, CAM, PLY ou DCM)
  const validateFilesHave3D = (files) => {
    if (files.length === 0) return false;
    return files.some(file => {
      const extension = file.filename ?
        file.filename.split('.').pop().toLowerCase() :
        file.originalFile?.name.split('.').pop().toLowerCase();
      return ['stl', 'cam', 'ply', 'dcm'].includes(extension);
    });
  };

  // Configuration de react-dropzone pour les uploads multiples
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'application/sla': ['.stl'],
      'application/octet-stream': ['.stl', '.cam', '.dcm', '.ply'],
      'application/pdf': ['.pdf'],
      'application/dicom': ['.dcm']
    },
    maxSize,
    multiple: true, // Permettre plusieurs fichiers
    maxFiles, // Nombre max de fichiers
    disabled: fileUploading || (files.length >= maxFiles),
    onDrop: async (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles.length > 0) {
        const error = rejectedFiles[0].errors[0];
        if (error.code === 'file-too-large') {
          setFileError('Un ou plusieurs fichiers sont trop volumineux. Taille max: 100MB');
        } else if (error.code === 'too-many-files') {
          setFileError(`Vous ne pouvez pas uploader plus de ${maxFiles} fichiers à la fois`);
        } else if (error.code === 'file-invalid-type') {
          setFileError('Format de fichier non supporté. Seuls les fichiers STL, CAM, PDF, DCM et PLY sont acceptés.');
        } else {
          setFileError('Erreur lors de l\'upload: ' + error.message);
        }
        return;
      }

      if (acceptedFiles.length > 0) {
        if (files.length + acceptedFiles.length > maxFiles) {
          setFileError(`Vous ne pouvez pas avoir plus de ${maxFiles} fichiers par soumission`);
          return;
        }

        setFileUploading(true);
        setFileError(null);
        setStlValidationError(false); // Réinitialiser l'erreur STL

        const newFiles = [...files];
        const newFileIds = [...fileIds];

        // Uploader chaque fichier séparément
        for (const file of acceptedFiles) {
          try {
            const response = await fileService.uploadFile(file);

            // Déterminer l'extension du fichier
            const extension = file.name.split('.').pop().toLowerCase();

            newFiles.push({
              id: response.file.id,
              originalFile: file,
              filename: file.name,
              extension: extension,
              size: file.size,
              dimensions: response.file.dimensions || {}
            });

            newFileIds.push(response.file.id);

            // Utiliser les dimensions du premier fichier STL comme dimensions principales
            if (extension === 'stl' && (!dimensions || Object.keys(dimensions).length === 0)) {
              setDimensions(response.file.dimensions || {});
            }
          } catch (error) {
            setFileError(error.response?.data?.message || 'Erreur lors de l\'upload d\'un fichier');
          }
        }

        setFiles(newFiles);
        setFileIds(newFileIds);
        setFileUploading(false);
      }
    }
  });

  // Chargement des données en cas d'édition
  useEffect(() => {
    if (id) {
      setIsEditing(true);
      dispatch(getSubmissionById(id));
    } else {
      setIsEditing(false);
      dispatch(clearSubmission()); // Nettoyer les données de la soumission précédente
      
      // Réinitialiser les états locaux également pour plus de sécurité
      setFiles([]);
      setFileIds([]);
      setFilesMarkedForRemoval([]);
    }
  }, [id, dispatch]);

  const [originalFileIds, setOriginalFileIds] = useState([]);
  // Mise à jour des champs avec les données existantes
  // Dans le useEffect qui charge les données d'édition
  useEffect(() => {
    if (isEditing && submission) {
      if (submission.files && submission.files.length > 0) {
        const loadedFiles = submission.files.map(file => ({
          id: file.id,
          filename: file.filename,
          extension: file.filename.split('.').pop().toLowerCase(),
          size: file.size,
          order: file.order,
          type: file.type,
          dimensions: file.dimensions || {}
        }));

        setFiles(loadedFiles);

        // Conserver les IDs des fichiers d'origine
        const loadedFileIds = loadedFiles.map(f => f.id);
        setFileIds(loadedFileIds);
        setOriginalFileIds(loadedFileIds); // NOUVEAU: conserver les IDs d'origine

        setDimensions(submission.dimensions || {});
      }
    } else {
      // En mode création, réinitialiser
      setOriginalFileIds([]);
    }
  }, [isEditing, submission]);

  // Fonction pour télécharger un fichier
  const handleDownloadFile = (fileId, filename) => {
    if (isEditing) {
      window.open(fileService.getDownloadUrl(fileId), '_blank');
    }
  };

  // Fonction pour supprimer un fichier
  const handleRemoveFile = (index) => {
    if (files.length <= 1) {
      setFileError('La soumission doit contenir au moins un fichier');
      return;
    }

    const fileToRemove = files[index];
    
    // Vérifier si la suppression de ce fichier laisserait la soumission sans fichier STL
    const remainingFiles = files.filter((_, i) => i !== index);
    const hasSTL = validateFilesHave3D(remainingFiles);
    
    if (!hasSTL) {
      setFileError('La soumission doit contenir au moins un fichier 3D (STL, CAM, PLY ou DCM)');
      setStlValidationError(true); // Activer l'erreur STL spécifique
      return;
    } else {
      setStlValidationError(false); // Désactiver l'erreur STL
    }

    if (isEditing) {
      // Au lieu de supprimer immédiatement, marquer le fichier pour suppression ultérieure
      setFilesMarkedForRemoval(prev => [...prev, fileToRemove.id]);

      // Mettre à jour l'interface visuellement
      const newFiles = files.filter((_, i) => i !== index);
      const newFileIds = fileIds.filter(fId => fId !== fileToRemove.id);

      setFiles(newFiles);
      setFileIds(newFileIds);
    } else {
      // En mode création, on supprime juste le fichier de l'état local
      const newFiles = files.filter((_, i) => i !== index);
      const newFileIds = fileIds.filter(fId => fId !== fileToRemove.id);

      setFiles(newFiles);
      setFileIds(newFileIds);
    }
  };
  
  // Fonction pour gérer le retour de manière contextuelle
  const handleReturn = () => {
    if (isEditing && id) {
      // Si on édite une soumission existante, retourner à sa page de détail
      navigate(getContextualPath(`submissions/${id}`));
    } else {
      // Si on crée une nouvelle soumission, retourner à la liste
      navigate(getContextualPath('submissions'));
    }
  };

  // Fonction pour soumettre le formulaire
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      // Réinitialiser les erreurs
      setFileError(null);
      setStlValidationError(false);
      
      if (files.length === 0) {
        setFileError('Veuillez télécharger au moins un fichier');
        setSubmitting(false);
        return;
      }

      // Vérifier qu'au moins un fichier STL est présent
      if (!validateFilesHave3D(files)) {
        setFileError('Votre soumission doit contenir au moins un fichier 3D (STL, CAM, PLY ou DCM)');
        setStlValidationError(true);
        console.log("Validation 3D échouée - La soumission nécessite au moins un fichier 3D");
        setSubmitting(false);
        return;
      }

      // Vérifier qu'un fichier principal est défini
      if (!files.some(file => file.type === 'main')) {
        // Définir le premier fichier comme principal
        const updatedFiles = [...files];
        updatedFiles[0].type = 'main';
        setFiles(updatedFiles);
      }

      // Mode création - envoyer tous les fileIds
      if (!isEditing) {
        const submissionData = {
          ...values,
          fileIds: fileIds,
          dimensions: dimensions
        };

        await dispatch(createSubmission(submissionData));
        navigate(getContextualPath('submissions'));
        return;
      }

      // MODE ÉDITION
      // 1. D'abord mettre à jour les informations de base de la soumission
      const submissionData = {
        ...values,
        dimensions: dimensions
      };

      await dispatch(updateSubmission(id, submissionData));

      // 2. Déterminer uniquement les fichiers ajoutés
      const addedFileIds = fileIds.filter(id => !originalFileIds.includes(id));

      let hasErrors = false;

      // 3. Associer les nouveaux fichiers
      if (addedFileIds.length > 0) {
        for (const fileId of addedFileIds) {
          try {
            await submissionService.addFileToSubmission(id, fileId);
          } catch (error) {
            const fileName = files.find(f => f.id === fileId)?.filename || fileId;
            console.error(`Erreur lors de l'association du fichier ${fileName}:`, error);
            setFileError(`Erreur lors de l'association du fichier ${fileName}`);
            hasErrors = true;
          }
        }
      }

      // 4. Supprimer les fichiers marqués pour suppression
      if (filesMarkedForRemoval.length > 0) {
        for (const fileId of filesMarkedForRemoval) {
          try {
            await submissionService.removeFileFromSubmission(id, fileId);
          } catch (error) {
            // Ignorer les erreurs 404 (fichier non trouvé)
            if (error.response && error.response.status !== 404) {
              console.error(`Erreur lors de la dissociation du fichier ${fileId}:`, error);
              setFileError(`Erreur lors de la suppression d'un fichier`);
              hasErrors = true;
            }
          }
        }
      }

      // 5. Mettre à jour le fichier principal si nécessaire
      const mainFile = files.find(file => file.type === 'main');
      if (mainFile) {
        try {
          await submissionService.setMainFile(id, mainFile.id);
        } catch (error) {
          console.error('Erreur lors de la définition du fichier principal:', error);
          // Non critique, ne pas bloquer la navigation
        }
      }

      if (!hasErrors) {
        navigate(getContextualPath(`submissions/${id}`));
      } else {
        setSubmitting(false);
      }
    } catch (error) {
      console.error('Erreur lors de la soumission:', error);
      setFileError('Une erreur s\'est produite lors de la soumission');
      setSubmitting(false);
    }
  };


  // Obtenir l'icône en fonction de l'extension du fichier
  const getFileIcon = (extension) => {
    switch (extension.toLowerCase()) {
      case 'stl':
        return 'view_in_ar';
      case 'cam':
        return 'precision_manufacturing';
      case 'pdf':
        return 'picture_as_pdf';
      default:
        return 'description';
    }
  };

  // Rendu des fichiers en grille
  const renderFilesGrid = () => {
    return (
      <div className="files-grid">
        {files.map((file, index) => (
          <div
            key={file.id}
            className={`file-card ${file.type === 'main' ? 'main-file' : ''} ${removingFiles.includes(file.id) ? 'removing' : ''}`}
            data-type={file.extension.toLowerCase()}
          >
            <div className="file-icon">
              <i className="material-icons">{getFileIcon(file.extension)}</i>
            </div>
            <div className="file-info">
              <div className="file-name" title={file.filename}>{file.filename}</div>
              <div className="file-meta">
                <span className="file-size">{(file.size / (1024 * 1024)).toFixed(2)} MB</span>
                <span className="file-type">{file.extension.toUpperCase()}</span>
              </div>
            </div>
            <div className="file-actions">
              {isEditing && (
                <button
                  type="button"
                  className="btn-icon btn-download"
                  title="Télécharger"
                  onClick={() => handleDownloadFile(file.id, file.filename)}
                >
                  <i className="material-icons">download</i>
                </button>
              )}
              <button
                type="button"
                className="btn-icon btn-delete"
                title="Supprimer"
                onClick={() => handleRemoveFile(index)}
                disabled={files.length <= 1 || removingFiles.includes(file.id)}
              >
                <i className="material-icons">delete</i>
              </button>
            </div>
          </div>
        ))}
        {files.length < maxFiles && (
          <div
            className={`add-file-card ${fileUploading ? 'uploading' : ''}`}
            {...getRootProps()}
          >
            <input {...getInputProps()} />
            {fileUploading ? (
              <div className="spinner"></div>
            ) : (
              <>
                <i className="material-icons">add</i>
                <span>Ajouter un fichier</span>
                <small>STL, CAM, PLY, DCM, PDF (max. 100MB)</small>
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="submission-form-container">
      <div className="page-header">
        <h1>{isEditing ? 'Modifier la soumission' : 'Nouvelle soumission'}</h1>
        <button
          className="btn btn-outline-secondary"
          onClick={handleReturn}
        >
          Retour
        </button>
      </div>

      <Formik
        initialValues={{
          name: submission?.name || '',
          description: submission?.description || '',
        }}
        validationSchema={SubmissionSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting, errors, touched }) => (
          <Form className="submission-form">
            <div className="form-section">
              <h2>Informations</h2>
              <div className="form-group">
                <label htmlFor="name">Nom de la soumission *</label>
                <Field
                  type="text"
                  name="name"
                  id="name"
                  className={`form-control ${errors.name && touched.name ? 'is-invalid' : ''}`}
                  placeholder="Nom de votre soumission"
                />
                <ErrorMessage name="name" component="div" className="invalid-feedback" />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description</label>
                <Field
                  as="textarea"
                  name="description"
                  id="description"
                  className={`form-control ${errors.description && touched.description ? 'is-invalid' : ''}`}
                  placeholder="Description détaillée de votre soumission"
                  rows="4"
                />
                <ErrorMessage name="description" component="div" className="invalid-feedback" />
              </div>
            </div>

            <div className="form-section">
              <h2>Fichiers *</h2>
              <div className="form-group">
                <div className="alert alert-info">
                  <i className="material-icons">info</i>
                  <span>La soumission doit contenir au moins un fichier 3D (STL, CAM, PLY ou DCM)</span>
                </div>
                
                {/* Affichage de l'erreur STL de manière plus visible */}
                {stlValidationError && (
                  <div className="alert alert-danger">
                    <i className="material-icons">error</i>
                    <span>Votre soumission doit contenir au moins un fichier 3D (STL, CAM, PLY ou DCM)</span>
                  </div>
                )}
                
                {files.length === 0 ? (
                  <div
                    {...getRootProps()}
                    className={`dropzone ${isDragActive ? 'active' : ''} ${fileError ? 'is-invalid' : ''}`}
                  >
                    <input {...getInputProps()} />
                    {fileUploading ? (
                      <div className="dropzone-loading">
                        <div className="spinner"></div>
                        <p>Upload en cours...</p>
                      </div>
                    ) : (
                      <div className="dropzone-message">
                        <i className="material-icons">cloud_upload</i>
                        <p>Glissez-déposez vos fichiers ici, ou cliquez pour sélectionner</p>
                        <span>Formats acceptés: STL, CAM, PLY, DCM, PDF — Taille max: {maxSize / (1024 * 1024)}MB — Max {maxFiles} fichiers</span>
                      </div>
                    )}
                  </div>
                ) : (
                  renderFilesGrid()
                )}
                
                {/* Afficher l'erreur générale dans un format plus visible */}
                {fileError && !stlValidationError && (
                  <div className="alert alert-danger mt-2">
                    <i className="material-icons">error</i>
                    <span>{fileError}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="form-actions">
              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={handleReturn}
              >
                Annuler
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isSubmitting || loading || fileUploading || files.length === 0}
              >
                {isSubmitting || loading ? 'Enregistrement...' : isEditing ? 'Mettre à jour' : 'Soumettre'}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default SubmissionForm;